import React, { useState, useRef, KeyboardEvent, ClipboardEvent, useEffect } from "react";
import Camera from '../../components/Camera';
import { IconQrcode } from '@tabler/icons-react';
import { useLogin } from "./Home.utils";
import InstallPrompt from "../../components/InstallPrompt";
import { useNavigate } from "react-router-dom";
import colors from "../../utils/colors";
import { motion } from "framer-motion";
import { ctaPulse, fadeIn, fadeInFromTop, heartbeat, staggerContainer } from "../../utils/animations";
import packageJson from '../../../package.json';

const CODE_DIGITS = 6;

const Home: React.FC = () => {
  const [showCamera, setShowCamera] = useState(false);
  const [code, setCode] = useState<string[]>(Array(CODE_DIGITS).fill(""));
  const inputsRef = useRef<HTMLInputElement[]>([]);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldAnimate(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < CODE_DIGITS - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("text").replace(/\D/g, "");

    if (pasteData.length > 0) {
      const newCode = pasteData.split("").slice(0, CODE_DIGITS);
      setCode([...newCode, ...Array(CODE_DIGITS - newCode.length).fill("")]);

      inputsRef.current[newCode.length - 1]?.focus();
    }
  };

  const isCodeComplete = code.every((digit) => digit !== "");

  const { login } = useLogin();
  const navigate = useNavigate();

  const handleCodeSubmit = () => {
    const finalCode = code.join("");

    //console.log("Submitted code:", finalCode);
    login(finalCode);
  };


  return (
    <motion.div 
      className="flex flex-col min-h-screen bg-gradient-to-r from-customBg via-customBg2 to-customBg/80"
      variants={staggerContainer}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
    >
      <main className="flex flex-col items-center justify-center flex-1 p-4 mb-14">

        <InstallPrompt/>

        <motion.img 
          src="/36minutlogo.png" 
          alt="Logo aplikacji" 
          className="mb-6 w-64 h-64" 
          //onClick={() => {window.open('https://36minut.pl', '_blank');}} 
          //style={{cursor: 'pointer'}}
          variants={fadeInFromTop}
        />

        <motion.div variants={fadeIn}>
          <motion.button
            variants={ctaPulse}
            animate="animate"
          className="text-white bg-gradient-to-br from-custom36p via-custom36s/60 to-custom36s hover:bg-gradient-to-r focus:ring-4 focus:outline-none focus:ring-custom36p shadow-lg shadow-custom36p/50 font-medium rounded-lg border-none text-lg px-5 py-2.5 text-center tracking-wide"
          onClick={() => setShowCamera(true)}
          whileTap={{ scale: 0.95 }}
          >
          <div className="flex items-center justify-center"> 
            <IconQrcode className="mr-2" />
              SKANUJ KOD QR
            </div>
          </motion.button>
        </motion.div>

        <motion.p className="mb-3 text-lg font-medium text-gray-500" variants={fadeIn}>lub</motion.p>

        <motion.div className="flex mb-2 space-x-2" variants={fadeIn}>
          {code.map((digit, index) => (
            <div
              key={index}
            >
              <input
                ref={(el) => (inputsRef.current[index] = el!)}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="
                  block
                  w-12 h-12
                  py-3
                  text-xl font-extrabold text-center
                  bg-gradient-to-b from-custom36p via-custom36s/70 to-custom36s
                  text-gray-900 dark:text-white
                  hover:bg-gradient-to-b focus:ring-4 focus:outline-none focus:ring-custom36p shadow-lg shadow-custom36p/50
                  rounded-lg
                  border-none
                "
              />
            </div>
          ))}
        </motion.div>

        <motion.p className="text-sm font-medium text-gray-500" variants={fadeIn}>Wpisz kod widoczny na terminalu</motion.p>
        <motion.p className="text-xs text-gray-400" variants={fadeIn}>v.{packageJson.version}</motion.p>

        <motion.div className="mt-2 min-h-[50px]" variants={fadeIn}>
          {isCodeComplete && (
            <motion.button
              variants={heartbeat}
              animate="animate"
              className={`text-white bg-gradient-to-br from-custom36p via-custom36s/60 to-custom36s hover:bg-gradient-to-r focus:ring-4 focus:outline-none focus:ring-custom36p shadow-lg shadow-custom36p/50 font-medium rounded-lg border-none text-sm px-3 py-1 text-center tracking-widest ${
                isCodeComplete ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
              }`}
              onClick={handleCodeSubmit}
              whileTap={{ scale: 0.95 }}
            >
                ZATWIERDŹ
            </motion.button>
          )}
        </motion.div>

      </main>
      {showCamera && <Camera onClose={() => setShowCamera(false)} />}
    </motion.div>
  );
};

export default Home;
